/**
 * ItemOptionsSheet — THE HOST'S LONG-PRESS SHEET, drawn by the studio host.
 *
 * A long press on a native cell (right-click / a 500ms hold — see
 * `viewir/components/ui.js longPressHandlers`) emits `removeRequested`, the
 * blueprint wires that to `host.itemOptions`, and the HOST answers by opening
 * THIS. It never deletes on the gesture alone: `host.crudRemove` is the delete
 * PRIMITIVE this sheet's Delete tile calls, not a long-press target.
 *
 * It is lifted from the legacy WebView runtime's own sheet
 * (`components/widget/WidgetRuntime.jsx ItemOptionsSheet`) so the two web
 * surfaces read the same, and it mirrors the phone's sheet
 * (`widgets/widget_runtime_host_actions.dart showWidgetRuntimeItemOptions`)
 * tile for tile: **Save** when the payload carries an `image_url` or
 * `video_url`, then **Delete** in destructive red, then **Cancel**. When both
 * exist the video wins because the image is its poster. The destructive row
 * IS the confirm — the standard action-sheet pattern on both kits.
 *
 * It lives in `studio/` and not in `viewir/components/` on purpose: it is HOST
 * CHROME, like the '+' and the back chevron the pane draws over the phone. No
 * blueprint may declare it, and the app draws its own.
 */

const INK = '#1c1c1e';
const MUTED = '#8e8e93';
const DESTRUCTIVE = '#FF3B30';
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif';

/**
 * WHAT THE SHEET IS ABOUT, in the payload's own words.
 *
 * A logged row says `item_id`; an in-flight capture says `grid_id`/
 * `session_id` and has no row at all — and the phone's sheet titles those two
 * differently for the same reason the delete resolver treats them differently
 * (one drops a row, the other drops the session). A caller that knows the row's
 * own name passes it as `title`.
 */
function itemOptionsTitle(payload) {
  const p = payload || {};
  if (p.item_id ?? p.entry_id) return 'this item';
  return p.session_id || p.grid_id ? 'this capture' : 'this item';
}

export default function ItemOptionsSheet({
  payload = null, title = null, busy = false, onDelete, onSave = null, onCancel,
}) {
  if (!payload) return null;
  const imageUrl = typeof payload.image_url === 'string' && payload.image_url ? payload.image_url : null;
  const videoUrl = typeof payload.video_url === 'string' && payload.video_url ? payload.video_url : null;
  const saveUrl = videoUrl || imageUrl;
  const tile = {
    width: '100%', border: 'none', background: 'transparent',
    padding: '15px 16px', fontSize: 17, fontWeight: 600, textAlign: 'center',
    cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1, fontFamily: FONT,
  };
  return (
    <div
      data-testid="native-item-options"
      onClick={busy ? undefined : onCancel}
      style={{
        position: 'absolute', inset: 0, zIndex: 60,
        background: 'rgba(0,0,0,0.32)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        padding: 10, fontFamily: FONT,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{
          background: 'rgba(248,248,250,0.96)', backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)', borderRadius: 14, overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 16px', textAlign: 'center', fontSize: 12, color: MUTED,
            borderBottom: '0.5px solid rgba(60,60,67,0.18)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {String(title || itemOptionsTitle(payload))}
          </div>
          {/* 有视频时保存视频而不是封面；否则沿用图片保存。 */}
          {saveUrl ? (
            <button
              type="button" disabled={busy} data-testid="native-item-options-save"
              onClick={() => onSave?.(saveUrl, payload)}
              style={{ ...tile, color: INK, borderBottom: '0.5px solid rgba(60,60,67,0.18)' }}
            >Save</button>
          ) : null}
          <button
            type="button" disabled={busy} data-testid="native-item-options-delete"
            onClick={() => onDelete?.(payload)}
            style={{ ...tile, color: DESTRUCTIVE }}
          >{busy ? 'Deleting…' : 'Delete'}</button>
        </div>
        <button
          type="button" disabled={busy} data-testid="native-item-options-cancel"
          onClick={busy ? undefined : onCancel}
          style={{
            ...tile, fontWeight: 700, color: INK, borderRadius: 14,
            background: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          }}
        >Cancel</button>
      </div>
    </div>
  );
}
